/**
 * AccessRow — one line of a settings list: what it is on the left, the
 * control that changes it on the right.
 *
 * It exists so the Access & login tab reads as ONE list. Before this, the
 * hostname field was a form row and the two policy switches were free-floating
 * tinted boxes with their own margins, so the page looked like three unrelated
 * fragments stacked up. Same grammar for every setting, one divider between
 * them, and the panel supplies the padding.
 */

import type { ReactNode } from 'react';

interface Props {
  label: ReactNode;
  hint?: ReactNode;
  /** Extra explanation shown UNDER the row, full width — for consequences
   *  that only apply once the control is on (e.g. "descendants inherit this"). */
  footnote?: ReactNode;
  children: ReactNode;
  /** Puts the control hard right and vertically centred (switches). Fields
   *  stretch instead, so an input can use the whole column. */
  compact?: boolean;
  testId?: string;
}

export default function AccessRow({ label, hint, footnote, children, compact, testId }: Props) {
  return (
    <div className="sa-access-row" data-testid={testId}>
      <div className="sa-access-label">
        <span className="sa-access-name">{label}</span>
        {hint && <span className="sa-access-hint">{hint}</span>}
      </div>
      <div className={`sa-access-control${compact ? ' sa-access-control--end' : ''}`}>{children}</div>
      {footnote && <p className="sa-access-footnote">{footnote}</p>}
    </div>
  );
}
